import { ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterContentInit, AfterViewInit, Component, ComponentRef, ContentChild, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormContainerSize, ItemWithID } from '@sb/library/base';
import { Observable, map, startWith, takeUntil } from 'rxjs';
import { FormFieldInputContainerComponent } from '../../../../container/form-field-container.component';
import { FormFieldSelectBaseComponent } from '../../form-field-select-base.component';
import { FormFieldSelectItemComponent, IItemGroup } from '../../form-field-select-item.component';
import { ChipPreviewComponent } from '../chip-preview/form-field-chip-preview.component';

@Component({
    selector: 'lib-input-select-chips',
    templateUrl: './form-field-select-chips.component.html',
    styleUrls: ['./form-field-select-chips.component.scss'],
})
export class FormFieldSelectChipsComponent<T extends ItemWithID | string> extends FormFieldSelectBaseComponent<T, T[]> implements OnInit, AfterContentInit, AfterViewInit {

    @ViewChild(MatAutocompleteTrigger, {static: false}) autocomplete!: MatAutocompleteTrigger;
    @ViewChild('input', {static: false}) input?: ElementRef;
    @ContentChild('chipPreview') chipPreviewTemplate!: TemplateRef<any>;

    private _filteredItemsByGroup!: Observable<Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>>;
    private _filteredItems!: Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>;
    private _selectedItems!: Set<FormFieldSelectItemComponent<T>>;
    private _remainingItemsByGroup!: Map<IItemGroup | null, Set<FormFieldSelectItemComponent<T>>>;
    private _optionSelectedForEnter!: FormFieldSelectItemComponent<T> | null;

    private _chipsPlaceholderText: string | undefined;
    private _overlayRef!: OverlayRef;

    @Input() showQuickSelect: boolean = true;
    @Input() size: FormContainerSize = FormContainerSize.Medium;
    @Input() allowChipDeletion: boolean = true;

    @Output() panelClosing: EventEmitter<void> = new EventEmitter();

    constructor(private _Overlay: Overlay) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.UpdatePlaceholderText();
    }

    //We have to override pristine else where since the view control is just the input text
    override ObservePristine() {
    }

    override SortItems(): void {
        super.SortItems();

        this._selectedItems = new Set(this.formControl.value.orderBy(value => this.nameOf!(value).toLocaleLowerCase()).map(item => this.itemMap.get(this.identifierOf(item))!));
        this._remainingItemsByGroup = new Map();
        this.sortedItemListByGroup.forEach((value, key) => {
            this._remainingItemsByGroup.set(key, new Set(value.filter(item => !this._selectedItems.has(item))));
        });

        this._filteredItemsByGroup = this.viewControl.valueChanges.pipe(
            startWith(''),
            map((value: string) => {
                const items = (value && typeof value == 'string') ? this.Filter(value) : this.allRemainingItems;
                if (items.length > 2000) {  // Limit autocomplete list to avoid performance degradation
                    items.length = 2000;
                }

                this._filteredItems = new Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>();
                this.sortedItemListByGroup?.forEach((_, group) => {
                    this._filteredItems.set(group, []);
                });

                items.forEach(item => {
                    this._filteredItems.get(item.group)!.push(item);
                });

                return this._filteredItems;
            })
        );
    }

    ngAfterViewInit(): void {
        this.RetryOptionSelectionSubscription();
    }

    private RetryOptionSelectionSubscription() {
        const interval = setInterval(() => {
            if (this.autocomplete) {
                this.autocomplete.optionSelections?.subscribe(data => {
                    this._optionSelectedForEnter = data.source.value;
                });
                clearInterval(interval); 
            }
        }, 250);
    }


    override writeValue(values: T[]) {
        this.WriteValue(values);
    }

    override UpdatePlaceHolder(): void {
        this._chipsPlaceholderText = this.placeholderText;
    }
    
    private WriteValue(values: T[], retrying: boolean = false) {
        if (this.sortedItemListByGroup?.size) {
            this._selectedItems = new Set(values.orderBy(value => this.nameOf!(value).toLocaleLowerCase()).map(value => this.itemMap.get(this.identifierOf(value))!));
            // we check to see if the selected chip is in list (list might not be populated yet)
            if (this._selectedItems.has(undefined as any)) {
                if (retrying) {
                    console.error('Tried to add chip that is not in the list');
                    this._selectedItems.delete(undefined as any);
                } else {
                    setTimeout(() => {
                        this.WriteValue(values, true);
                    });
                    return;
                }
            }
            this._remainingItemsByGroup = new Map();
            this.sortedItemListByGroup.forEach((value, key) => {
                this._remainingItemsByGroup.set(key, new Set(value.filter(item => !this._selectedItems.has(item))));
            });
            this.UpdateFormControl(false);
            this.DetectChanges();
        } 
    }

    Add(event: MatChipInputEvent): void {
        if (!event.value) {
            return;
        }
        this._chipsPlaceholderText = '';
        const matches = this.Filter(event.value);
        if (matches.length == 1) {
            this._selectedItems.add(matches[0]);
            //this._remainingItemsByGroup.get(matches[0].group)!.delete(matches[0]);
            this.UpdateFormControl();
            this.DetectChanges();
        }
    }

    Remove(item: FormFieldSelectItemComponent<T>) {
        this._selectedItems.delete(item);
        if (this._selectedItems.size === 0) {
            this._chipsPlaceholderText = this.placeholderText;
        }
        const remainingItemsInGroup = [...this._remainingItemsByGroup.get(item.group)!];
        remainingItemsInGroup.push(item);
        if (this.sorterOf) {
            remainingItemsInGroup.sort((a, b) => this.SortListWithNullValues(a,b));
        }
        this._remainingItemsByGroup.set(item.group, new Set(remainingItemsInGroup));

        this.UpdateFormControl();
    }

    Selected(item: FormFieldSelectItemComponent<T>) {
        this._remainingItemsByGroup.get(item.group)!.delete(item);
        this._selectedItems.add(item);
        this._chipsPlaceholderText = '';
        this.UpdateFormControl();
    }

    Clear() {
        if (!this.autocomplete.panelOpen) {
            this.viewControl.setValue('');
            this.autocomplete.closePanel();
            this.panelClosing.emit();
        }
    }

    Blur() {
        this.input?.nativeElement?.blur();
        this.viewControl.setValue('');
        this.autocomplete.closePanel();
        this.panelClosing.emit();

    }

    PanelOpened() {
        this.autocomplete.panelClosingActions.pipe(takeUntil(this.autocomplete.autocomplete.closed)).subscribe(_ => {
            this.viewControl.setValue('');
            this.panelClosing.emit();
        });
    }

    Clicked(event: any) {
        if (!this.isDisabled) {
            event.preventDefault();
            this.input?.nativeElement?.focus();
            this.autocomplete.openPanel();
        }
    }

    ClearAll() {
        this._selectedItems = new Set();
        this._remainingItemsByGroup = new Map();
        this.sortedItemListByGroup.forEach((value, key) => {
            this._remainingItemsByGroup.set(key, new Set(value));
        });

        this.UpdateFormControl();
        this.DetectChanges();
    }

    SelectAll() {
        const selectedItems: FormFieldSelectItemComponent<T>[] = [];
        this._remainingItemsByGroup = new Map();

        this.sortedItemListByGroup.forEach((value, key) => {
            this._remainingItemsByGroup.set(key, new Set());
            selectedItems.push(...value);
        });

        this._selectedItems = new Set(selectedItems);

        this.UpdateFormControl();
        this.DetectChanges();
    }

    // Called from search modal to close when event.stopPropagation is used
    CloseIfNotFocused() {
        const activeElement = document.activeElement;
        if ((!activeElement || activeElement !== this.input?.nativeElement) && this.autocomplete.panelOpen) {
            this.autocomplete.closePanel();
        }
    }

    ChipSelected(item: FormFieldSelectItemComponent<T>, event: any) {
        if (!this.chipPreviewTemplate) {
            event.stopPropagation();
            return;
        }
        const element = event.currentTarget as HTMLElement;
        this._overlayRef = this._Overlay.create(
            {
            maxWidth: '300px',
            maxHeight: '100px',
            hasBackdrop: true,
            positionStrategy: this._Overlay.position().flexibleConnectedTo(element).withPositions(
                [
                    {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 10,
            }, {
                originX: 'start',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom',
                offsetY: -10,
            }])
        });

        const component = new ComponentPortal(ChipPreviewComponent<T>);
        const componentRef: ComponentRef<ChipPreviewComponent<T>> = this._overlayRef.attach(component);
        componentRef.instance.data = item.value;
        componentRef.instance.chipPreviewContentTemplate = this.chipPreviewTemplate;

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef?.dispose();
        });
        event.stopPropagation();
    }

    private UpdateFormControl(markControlDirty: boolean = true): void {
        this.viewControl.setValue('');
        this.formControl.isDirty = markControlDirty;
        this.SetFormControlValue([...this._selectedItems].map(item => item.value));
        this.UpdatePlaceholderText();
        this.OnBlur();
    }

    private Filter(value: string): FormFieldSelectItemComponent<T>[] {
        const checkValue = value.trim().toLocaleLowerCase();
        return this.allRemainingItems.filter(item => this.nameOf!(item.value)?.toLocaleLowerCase().includes(checkValue));
    }

    private get allRemainingItems(): FormFieldSelectItemComponent<T>[] {
        const result: FormFieldSelectItemComponent<T>[] = [];

        this._remainingItemsByGroup?.forEach(value => {
            result.push(...[...value]);
        });

        return result;
    }

    private UpdatePlaceholderText(): void {
        if (!this._selectedItems && this.formControl.value.length === 0 || this._selectedItems && this._selectedItems.size === 0) {
            this._chipsPlaceholderText = this.placeholderText;
        }
    }

    get filteredItemsByGroup(): Observable<Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>> {
        return this._filteredItemsByGroup;
    }

    get selectedItems(): Set<FormFieldSelectItemComponent<T>> {
        return this._selectedItems;
    }

    get separatorKeyCodes(): number[] {
        return [ENTER, SEMICOLON];
    }

    get chipsPlaceholderText(): string | undefined {
        return this._chipsPlaceholderText;
    }

    @HostListener('document:keydown.enter', ['$event'])
    onKeydownHandler(event: KeyboardEvent) {
        event.stopPropagation();
        if ((this.autocomplete?.panelOpen && this._filteredItems.size === 1) || this._optionSelectedForEnter) {
            let result: FormFieldSelectItemComponent<T>[] = []
            this._filteredItems.forEach(value => result.push(...[...value]))
            if (this._optionSelectedForEnter) {
                this.Selected(this._optionSelectedForEnter);
            } else {
                this.Selected(result[0]);
            }
            this._optionSelectedForEnter = null;
        }
    }

}
